/**
 * Chart SVG builder shared by the viewer (React, via innerHTML) and the PDF
 * renderer. Pure string output, brand colour in, no dependencies. Sizes use
 * a 1000×560 viewBox so cqw-based containers scale it.
 */
import { EXTRA_TYPES, extraChartSvg, extendPalette, type Axes, type Milestone } from './chartCatalog';

export interface ChartSpec {
  /** One of the five basics here or any id in CHART_CATALOG (chartCatalog.ts). */
  type?: string;
  labels?: string[];
  datasets?: Array<{ label?: string; values?: number[]; /** 'percent' → values are already ×100 and get a % sign. */ format?: string; /** Line charts: draw this series dashed (a benchmark, a plan). */ dashed?: boolean }>;
  /** Scale end for gauge / progress / rings (default 100 for percentages). */
  max?: number;
  /** Unit written after plain numbers ("SEK", "posts"). */
  unit?: string;
  /** Series / slice colours in order (#hex), when a deck fixes a colour code (e.g. organic purple, paid green). Default: brand palette. */
  colors?: string[];
  /** Column / bar: the category (index, 0-based) or categories to stand out in the accent colour; the rest stay primary. */
  highlight?: number | number[];
  /** Column / line: from this category index on the values are a forecast — lighter fill, "Actuals / Forecast" legend. */
  forecastFrom?: number;
  /** A short callout pinned top-right of the plot ("CAGR +12%", "Launch week"). */
  annotation?: string;
  /** matrix / risk: axis titles, low/high words or level names. */
  axes?: Axes;
  /** gantt: markers under the axis. */
  milestones?: Milestone[];
  /** matrix, and single-series bar / column: one group word per label, for colour and legend. */
  groups?: string[];
}

/** Which categories a `highlight` picks out. */
function highlighted(h: ChartSpec['highlight']): Set<number> {
  return new Set((Array.isArray(h) ? h : h == null ? [] : [h]).map(Number).filter((n) => Number.isInteger(n) && n >= 0));
}

/** Column / line / bar options that PowerPoint's native charts cannot show — such charts export as images. */
export function needsImage(chart: ChartSpec): boolean {
  return highlighted(chart.highlight).size > 0 || Number.isInteger(chart.forecastFrom) || !!chart.annotation || (chart.datasets || []).some((d) => d && d.dashed) || (Array.isArray(chart.groups) && chart.groups.length > 0 && (chart.datasets || []).length === 1);
}

/** Single-series bar / column coloured by group ("Commuter rail", "Bus"): the group name per category, and its legend. */
function groupsOf(spec: ChartSpec, seriesCount: number): { names: string[]; of: (i: number) => number } | null {
  if (seriesCount !== 1 || !Array.isArray(spec.groups) || !spec.groups.length) return null;
  const g = spec.groups.map((x) => String(x ?? '')); const names = [...new Set(g.filter(Boolean))];
  return names.length ? { names, of: (i) => Math.max(0, names.indexOf(g[i])) } : null;
}
function groupLegend(names: string[], palette: string[], c: ChartColors): string {
  let x = 70; let out = '';
  names.forEach((name, i) => { out += `<rect x="${x}" y="${H - 26}" width="18" height="18" rx="5" fill="${palette[i % palette.length]}"/><text x="${x + 26}" y="${H - 11}" fill="${c.textMuted}" font-size="18" ${FONT}>${esc(name)}</text>`; x += 60 + name.length * 10; });
  return out;
}

/** The callout pill in the top-right corner of a plot. */
function annotationSvg(txt: string | undefined, c: ChartColors, right: number, top: number): string {
  if (!txt) return '';
  const w = Math.min(420, 28 + txt.length * 10.5);
  return `<rect x="${right - w}" y="${top - 34}" width="${w}" height="30" rx="15" fill="${c.text}"/><text x="${right - w / 2}" y="${top - 13}" text-anchor="middle" fill="#fff" font-size="16" font-weight="700" ${FONT}>${esc(txt)}</text>`;
}

/** "Actuals / Forecast" legend for a split series. */
function forecastLegend(c: ChartColors, color: string, y: number): string {
  return `<rect x="70" y="${y}" width="18" height="18" rx="5" fill="${color}"/><text x="96" y="${y + 15}" fill="${c.textMuted}" font-size="18" ${FONT}>Actuals</text><rect x="190" y="${y}" width="18" height="18" rx="5" fill="${color}" opacity="0.45"/><text x="216" y="${y + 15}" fill="${c.textMuted}" font-size="18" ${FONT}>Forecast</text>`;
}

export interface ChartColors {
  primary: string;
  accent: string;
  text: string;
  textMuted: string;
  grid: string;
  /** Extra series / pie slices. */
  palette?: string[];
}

function esc(s: unknown): string {
  return String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
}

/** Values are shares when the dataset says so — show them with a % sign. */
function isPct(d?: { label?: string; format?: string }): boolean {
  return d?.format === 'percent' || /share|percent|pct|%/i.test(String(d?.label || ''));
}
function valueFmt(v: number, d?: { label?: string; format?: string }): string {
  return isPct(d) ? `${fmt(v)}%` : fmt(v);
}

function fmt(v: number): string {
  if (!Number.isFinite(v)) return '';
  if (Math.abs(v) >= 1_000_000) return `${Math.round(v / 100_000) / 10}M`;
  if (Math.abs(v) >= 10_000) return `${Math.round(v / 1000)}k`;
  if (Math.abs(v) >= 1000) return `${Math.round(v / 100) / 10}k`;
  return Number.isInteger(v) ? String(v) : String(Math.round(v * 10) / 10);
}

const W = 1000;
/** Drawing height in viewBox units. Slides use the 1000×560 default; dashboards pass the height that
 *  matches their card box so a wide card gets a wide, short chart instead of a letterboxed one. */
let H = 560;
/** Final font scale the host applies to the SVG text (dashboards rescale labels to ~11px); bar layout
 *  uses it so the label column and value gutter are sized for the text as it will actually render. */
let FS = 1;
const FONT = 'font-family="inherit"';

export function chartSvg(chart: ChartSpec, cIn: ChartColors, opts?: { height?: number; fontScale?: number }): string {
  let c = cIn;
  H = Math.max(100, Math.min(1200, opts?.height ?? 560));
  FS = opts?.fontScale ?? 1;
  const labels = chart.labels ?? [];
  const series = (chart.datasets ?? []).filter((d) => Array.isArray(d.values));
  const fixed = Array.isArray(chart.colors) ? chart.colors.filter((x) => typeof x === 'string' && /^#?[0-9a-f]{3,8}$/i.test(x)).map((x) => (x.startsWith('#') ? x : `#${x}`)) : [];
  // Fixed colours first, then the brand palette (or primary + accent) extended from the brand's own hues.
  const palette = extendPalette([...fixed, ...(c.palette && c.palette.length ? c.palette : [c.primary, c.accent])]);
  c = { ...c, palette };
  const type = chart.type ?? 'bar';
  if (EXTRA_TYPES.has(type)) return extraChartSvg(type, chart, c, { W, H, FS });
  if (!labels.length || !series.length) {
    return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg"><text x="${W / 2}" y="${H / 2}" text-anchor="middle" fill="${c.textMuted}" font-size="28" ${FONT}>No data yet</text></svg>`;
  }
  if (type === 'pie' || type === 'donut') return pieSvg(labels, series[0].values ?? [], palette, c, type === 'donut');
  if (type === 'line') return lineSvg(labels, series, palette, c, chart);
  if (type === 'column') return columnSvg(labels, series, palette, c, chart);
  return barSvg(labels, series, palette, c, chart);
}

function barSvg(labels: string[], series: ChartSpec['datasets'] & object, palette: string[], c: ChartColors, spec: ChartSpec = {}): string {
  const s = series as Array<{ label?: string; values?: number[]; format?: string }>;
  const hl = highlighted(spec.highlight); const grp = groupsOf(spec, s.length);
  const max = Math.max(1, ...s.flatMap((d) => d.values ?? []));
  const rows = labels.length;
  const legendH = s.length > 1 ? Math.min(40, H * 0.22) : 0;
  const pad = Math.min(20, H * 0.08);
  const rowH = Math.min(70, (H - pad * 2 - legendH) / rows);
  // Label column grows with the longest category (capped) and long names get an ellipsis + tooltip instead of being clipped.
  const fs = rows > 10 ? 16 : 20;
  const cw = fs * FS * 0.52;
  const longest = Math.max(1, ...labels.map((l) => String(l ?? '').length));
  const labelW = Math.min(340, Math.max(FS === 1 ? 180 : 90, Math.ceil(longest * cw) + 30));
  const valueW = FS === 1 ? 110 : Math.ceil(20 * FS * 4.2) + 30;
  const maxChars = Math.max(6, Math.floor((labelW - 30) / cw));
  const barsPerRow = s.length;
  const gap = Math.min(16, rowH * 0.3);
  const barH = Math.max(4, (rowH - gap) / barsPerRow);
  const barInner = Math.max(3, barH - Math.min(4, barH * 0.2));
  const rx = Math.min(6, barInner / 2);
  let out = '';
  labels.forEach((label, i) => {
    const y = pad + i * rowH;
    const full = String(label ?? '');
    const shown = full.length > maxChars ? `${full.slice(0, maxChars - 1)}…` : full;
    out += `<text x="${labelW - 16}" y="${(y + rowH / 2 + fs * FS * 0.36).toFixed(1)}" text-anchor="end" fill="${c.textMuted}" font-size="${fs}" font-weight="500" ${FONT}><title>${esc(full)}</title>${esc(shown)}</text>`;
    s.forEach((d, si) => {
      const v = d.values?.[i] ?? 0;
      const w = Math.max(6, (v / max) * (W - labelW - valueW));
      const by = y + gap / 2 + si * barH;
      const base = grp ? palette[grp.of(i) % palette.length] : palette[si % palette.length];
      const fill = hl.size ? (hl.has(i) ? c.accent : base) : base;
      out += `<rect x="${labelW}" y="${by.toFixed(1)}" width="${w.toFixed(1)}" height="${barInner.toFixed(1)}" rx="${rx.toFixed(1)}" fill="${fill}"${hl.size && !hl.has(i) ? ' opacity="0.75"' : ''}/>`;
      out += `<text x="${(labelW + w + 12).toFixed(1)}" y="${(by + barInner / 2 + 20 * FS * 0.36).toFixed(1)}" fill="${c.text}" font-size="20" font-weight="700" ${FONT}>${esc(valueFmt(v, s[si]))}</text>`;
    });
  });
  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">${out}${grp ? groupLegend(grp.names, palette, c) : legend(s, palette, c)}${annotationSvg(spec.annotation, c, W - 30, 54)}</svg>`;
}

function columnSvg(labels: string[], series: ChartSpec['datasets'] & object, palette: string[], c: ChartColors, spec: ChartSpec = {}): string {
  const s = series as Array<{ label?: string; values?: number[]; format?: string }>;
  const hl = highlighted(spec.highlight); const fc = Number.isInteger(spec.forecastFrom) ? Number(spec.forecastFrom) : -1; const grp = groupsOf(spec, s.length);
  const axisSfx = s.every((d) => isPct(d)) ? '%' : '';
  const max = Math.max(1, ...s.flatMap((d) => d.values ?? []));
  const top = Math.min(40, H * 0.16), bottomM = Math.min(70, H * (s.length > 1 ? 0.36 : 0.26));
  const left = 70, bottom = H - bottomM, xLabelY = bottom + Math.min(28, bottomM * 0.45);
  const groupW = (W - left - 30) / labels.length;
  const colW = Math.max(10, (groupW * 0.7) / s.length);
  let out = '';
  for (let g = 0; g <= 4; g++) {
    const y = top + ((bottom - top) * g) / 4;
    out += `<line x1="${left}" x2="${W - 30}" y1="${y}" y2="${y}" stroke="${c.grid}" stroke-width="1"/>`;
    out += `<text x="${left - 10}" y="${y + 6}" text-anchor="end" fill="${c.textMuted}" font-size="16" ${FONT}>${esc(fmt(max - (max * g) / 4))}${axisSfx}</text>`;
  }
  labels.forEach((label, i) => {
    const gx = left + i * groupW + (groupW - colW * s.length) / 2;
    s.forEach((d, si) => {
      const v = d.values?.[i] ?? 0;
      const h = Math.max(4, (v / max) * (bottom - top));
      const x = gx + si * colW;
      const base = grp ? palette[grp.of(i) % palette.length] : palette[si % palette.length]; const fill = hl.size && hl.has(i) ? c.accent : base;
      out += `<rect x="${x}" y="${bottom - h}" width="${colW - 4}" height="${h}" rx="6" fill="${fill}"${fc >= 0 && i >= fc ? ' opacity="0.45"' : hl.size && !hl.has(i) ? ' opacity="0.75"' : ''}/>`;
      out += `<text x="${x + (colW - 4) / 2}" y="${bottom - h - 8}" text-anchor="middle" fill="${c.text}" font-size="16" font-weight="700" ${FONT}>${esc(valueFmt(v, s[si]))}</text>`;
    });
    // Long handles / many columns: smaller type and an ellipsis so labels never collide.
    const maxChars = Math.max(6, Math.floor(groupW / (labels.length >= 6 ? 8.5 : 10)));
    const shown = label.length > maxChars ? `${label.slice(0, maxChars - 1)}…` : label;
    out += `<text x="${left + i * groupW + groupW / 2}" y="${xLabelY.toFixed(1)}" text-anchor="middle" fill="${c.textMuted}" font-size="${labels.length >= 6 || label.length > 12 ? 14 : 18}" font-weight="500" ${FONT}><title>${esc(label)}</title>${esc(shown)}</text>`;
  });
  if (fc > 0 && fc < labels.length) out += `<line x1="${left + fc * groupW}" x2="${left + fc * groupW}" y1="${top - 6}" y2="${bottom}" stroke="${c.textMuted}" stroke-width="2" stroke-dasharray="6 6"/>`;
  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">${out}${grp ? groupLegend(grp.names, palette, c) : fc >= 0 && s.length < 2 ? forecastLegend(c, palette[0], H - 26) : legend(s, palette, c)}${annotationSvg(spec.annotation, c, W - 30, top)}</svg>`;
}

function lineSvg(labels: string[], series: ChartSpec['datasets'] & object, palette: string[], c: ChartColors, spec: ChartSpec = {}): string {
  const s = series as Array<{ label?: string; values?: number[]; format?: string; dashed?: boolean }>;
  const fc = Number.isInteger(spec.forecastFrom) ? Number(spec.forecastFrom) : -1;
  const axisSfx = s.every((d) => isPct(d)) ? '%' : '';
  const max = Math.max(1, ...s.flatMap((d) => d.values ?? []));
  const top = Math.min(40, H * 0.16), bottomM = Math.min(70, H * (s.length > 1 ? 0.36 : 0.26));
  const left = 70, right = W - 30, bottom = H - bottomM, xLabelY = bottom + Math.min(28, bottomM * 0.45);
  const stepX = labels.length > 1 ? (right - left) / (labels.length - 1) : 0;
  let out = '';
  for (let g = 0; g <= 4; g++) {
    const y = top + ((bottom - top) * g) / 4;
    out += `<line x1="${left}" x2="${right}" y1="${y}" y2="${y}" stroke="${c.grid}" stroke-width="1"/>`;
    out += `<text x="${left - 10}" y="${y + 6}" text-anchor="end" fill="${c.textMuted}" font-size="16" ${FONT}>${esc(fmt(max - (max * g) / 4))}${axisSfx}</text>`;
  }
  labels.forEach((label, i) => {
    out += `<text x="${left + i * stepX}" y="${xLabelY.toFixed(1)}" text-anchor="middle" fill="${c.textMuted}" font-size="18" font-weight="500" ${FONT}>${esc(label)}</text>`;
  });
  s.forEach((d, si) => {
    const pts = labels.map((_, i) => `${left + i * stepX},${bottom - ((d.values?.[i] ?? 0) / max) * (bottom - top)}`);
    const color = palette[si % palette.length];
    if (fc > 0 && fc < pts.length && !d.dashed) {
      // Actuals solid up to the split, forecast dashed from there.
      out += `<polyline points="${pts.slice(0, fc).join(' ')}" fill="none" stroke="${color}" stroke-width="5" stroke-linejoin="round" stroke-linecap="round"/>`;
      out += `<polyline points="${pts.slice(fc - 1).join(' ')}" fill="none" stroke="${color}" stroke-width="5" stroke-linejoin="round" stroke-linecap="round" stroke-dasharray="10 10" opacity="0.7"/>`;
    } else out += `<polyline points="${pts.join(' ')}" fill="none" stroke="${color}" stroke-width="${d.dashed ? 4 : 5}" stroke-linejoin="round" stroke-linecap="round"${d.dashed ? ' stroke-dasharray="10 10"' : ''}/>`;
    labels.forEach((_, i) => {
      const [x, y] = pts[i].split(',').map(Number);
      out += `<circle cx="${x}" cy="${y}" r="7" fill="#fff" stroke="${color}" stroke-width="4"/>`;
      if (si === 0) out += `<text x="${x}" y="${y - 14}" text-anchor="middle" fill="${c.text}" font-size="16" font-weight="700" ${FONT}>${esc(fmt(d.values?.[i] ?? 0))}</text>`;
    });
  });
  if (fc > 0 && fc < labels.length) out += `<line x1="${left + (fc - 0.5) * stepX}" x2="${left + (fc - 0.5) * stepX}" y1="${top - 6}" y2="${bottom}" stroke="${c.textMuted}" stroke-width="2" stroke-dasharray="6 6"/>`;
  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">${out}${legend(s, palette, c)}${annotationSvg(spec.annotation, c, right, top)}</svg>`;
}

function pieSvg(labels: string[], values: number[], palette: string[], c: ChartColors, donut: boolean): string {
  const total = values.reduce((a, b) => a + (b || 0), 0) || 1;
  const r = Math.min(220, H / 2 - 40), ri = donut ? Math.round(r * 0.59) : 0, cx = r + 80, cy = H / 2;
  let angle = -Math.PI / 2;
  let out = '';
  labels.forEach((label, i) => {
    const v = values[i] || 0;
    const a = (v / total) * Math.PI * 2;
    const x1 = cx + r * Math.cos(angle), y1 = cy + r * Math.sin(angle);
    const x2 = cx + r * Math.cos(angle + a), y2 = cy + r * Math.sin(angle + a);
    const large = a > Math.PI ? 1 : 0;
    const color = palette[i % palette.length];
    if (v > 0) {
      if (donut) {
        const xi1 = cx + ri * Math.cos(angle + a), yi1 = cy + ri * Math.sin(angle + a);
        const xi2 = cx + ri * Math.cos(angle), yi2 = cy + ri * Math.sin(angle);
        out += `<path d="M${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} L${xi1},${yi1} A${ri},${ri} 0 ${large} 0 ${xi2},${yi2} Z" fill="${color}" stroke="#fff" stroke-width="3"/>`;
      } else {
        out += `<path d="M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} Z" fill="${color}" stroke="#fff" stroke-width="3"/>`;
      }
    }
    angle += a;
    const ly = 90 + i * 52;
    out += `<rect x="600" y="${ly - 16}" width="22" height="22" rx="6" fill="${color}"/>`;
    out += `<text x="636" y="${ly + 2}" fill="${c.text}" font-size="22" font-weight="600" ${FONT}>${esc(label)}</text>`;
    out += `<text x="${W - 40}" y="${ly + 2}" text-anchor="end" fill="${c.textMuted}" font-size="22" ${FONT}>${esc(fmt(v))} · ${Math.round((v / total) * 100)}%</text>`;
  });
  if (donut) out += `<text x="${cx}" y="${cy + 12}" text-anchor="middle" fill="${c.text}" font-size="40" font-weight="800" ${FONT}>${esc(fmt(total))}</text>`;
  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">${out}</svg>`;
}

function legend(s: Array<{ label?: string }>, palette: string[], c: ChartColors): string {
  if (s.length < 2) return '';
  let x = 70;
  let out = '';
  s.forEach((d, i) => {
    out += `<rect x="${x}" y="${H - 26}" width="18" height="18" rx="5" fill="${palette[i % palette.length]}"/>`;
    out += `<text x="${x + 26}" y="${H - 11}" fill="${c.textMuted}" font-size="18" ${FONT}>${esc(d.label || `Series ${i + 1}`)}</text>`;
    x += 60 + (d.label || `Series ${i + 1}`).length * 10;
  });
  return out;
}
